import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { useGreenhouseForm } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider';
import { INetZeroDetailFormValues } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider/types';
import NetZeroAcceptance from './NetZeroAcceptance';
import NetZeroBaseYear from './NetZeroBaseYear';
import NetZeroGoalYear from './NetZeroGoalYear';
import NetZeroScope from './NetZeroScope';
import {
  checkTermTargetComplete,
  createNewNetZeroDetail,
  mapNetZeroFormValues,
} from './utils';
import { NetZeroModalSchema } from './validators';

type ModalNetZeroProps = {
  open: boolean;
  mode: 'create' | 'edit';
  close: () => void;
  currentIndex?: number;
  editData?: INetZeroDetailFormValues;
};

const defaultFormValues = {
  scopeId: '',
  tgoId: '',
  sbtiId: '',
};

const ModalNetZero = ({
  mode,
  open,
  close,
  currentIndex,
  editData,
}: ModalNetZeroProps) => {
  const initialValue =
    mode === 'edit' && editData
      ? mapNetZeroFormValues(editData)
      : defaultFormValues;

  const modalNetZeroContext = useForm<INetZeroDetailFormValues>({
    defaultValues: initialValue,
    mode: 'onChange',
    resolver: zodResolver(NetZeroModalSchema),
  });

  const { handleSubmit, unregister, formState, watch, getValues } =
    modalNetZeroContext;
  const { setValue, control, trigger } = useGreenhouseForm();

  const { completedSection } = useWatch({ name: 'greenhouseGoalTerm' });
  const { isCompletedTh, isCompletedEn } = completedSection ?? {};

  const netZeroDetails = useWatch({
    control,
    name: 'greenhouseGoalTerm.netZero.details',
  });

  const [nearTermTarget, longTermTarget] = watch([
    'nearTermTarget',
    'longTermTarget',
  ]);

  const isNearTermTargetComplete = checkTermTargetComplete(nearTermTarget);
  const isLongTermTargetComplete = checkTermTargetComplete(longTermTarget);
  const isGoalYearComplete =
    isNearTermTargetComplete || isLongTermTargetComplete;

  const handleClose = () => {
    unregister();
    close();
  };

  const handleSave = () => {
    const formData = getValues();
    const updateNetZeroDetails = [...(netZeroDetails || [])];
    const newNetZeroDetail = createNewNetZeroDetail(formData);

    if (mode === 'create') {
      updateNetZeroDetails.push(newNetZeroDetail);
    }

    if (
      mode === 'edit' &&
      currentIndex !== undefined &&
      currentIndex !== null
    ) {
      updateNetZeroDetails[currentIndex] = newNetZeroDetail;
    }

    setValue('greenhouseGoalTerm.netZero.details', updateNetZeroDetails);
    trigger('greenhouseGoalTerm.netZero.details');
    handleClose();
  };

  return (
    <FormProvider {...modalNetZeroContext}>
      <Dialog open={open} maxWidth="md">
        <DialogTitle fontWeight={800} fontSize={16}>
          {mode === 'create' ? 'เพิ่ม' : 'แก้ไข'}
          รายละเอียดการตั้งเป้าหมายการปล่อยก๊าซเรือนกระจกสุทธิเป็นศูนย์
          (Net-Zero Greenhouse Gas Emissions)
        </DialogTitle>
        <DialogContent>
          <Typography fontWeight={800}>ขอบเขตการปล่อยก๊าซเรือนกระจก</Typography>
          <NetZeroScope control={control} mode={mode} />
          <Typography fontWeight={800} mt={4}>
            เป้าหมายลดการปล่อยก๊าซเรือนกระจกเทียบกับปีฐาน
          </Typography>
          <NetZeroBaseYear
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
          <NetZeroGoalYear
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
          <NetZeroAcceptance
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button color="inherit" onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit(handleSave)}
            disabled={!formState.isValid || !isGoalYearComplete}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default ModalNetZero;
